import { useState, useEffect, useCallback } from 'react';
import { scoringApi, ScoringCalculateParams, ScoringRankingParams } from '@/api/analysis';
import type {
  ScoringRule,
  ScoringRuleCreate,
  ScoringRuleItem,
  ScoringResultResponse,
  ScoreRankingResponse,
} from '@/types/data';
import { useAuthStore } from '@/stores/authStore';

type ViewMode = 'rules' | 'calculate' | 'ranking';

export default function Scoring() {
  const { accessibleCompanies } = useAuthStore();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('rules');

  // ルール管理
  const [rules, setRules] = useState<ScoringRule[]>([]);
  const [isLoadingRules, setIsLoadingRules] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState<ScoringRule | null>(null);

  // ルール作成/編集フォーム
  const [ruleName, setRuleName] = useState('');
  const [ruleDescription, setRuleDescription] = useState('');
  const [ruleItems, setRuleItems] = useState<ScoringRuleItem[]>([
    { label_pattern: '', score: 0, category: '' },
  ]);

  // スコア計算
  const [selectedRuleId, setSelectedRuleId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [scoreResult, setScoreResult] = useState<ScoringResultResponse | null>(null);
  const [rankingResult, setRankingResult] = useState<ScoreRankingResponse | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // 初期化
  useEffect(() => {
    if (accessibleCompanies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(accessibleCompanies[0].id);
    }
  }, [accessibleCompanies, selectedCompanyId]);

  // ルール一覧取得
  const fetchRules = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoadingRules(true);
    try {
      const data = await scoringApi.getRules(selectedCompanyId);
      setRules(data);
      if (data.length > 0 && !selectedRuleId) {
        setSelectedRuleId(data[0].id);
      }
    } catch (error) {
      console.error('スコアルール取得エラー:', error);
    } finally {
      setIsLoadingRules(false);
    }
  }, [selectedCompanyId, selectedRuleId]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  // ルール作成/編集モーダルを開く
  const openRuleModal = (rule?: ScoringRule) => {
    if (rule) {
      setEditingRule(rule);
      setRuleName(rule.name);
      setRuleDescription(rule.description || '');
      setRuleItems(rule.rules.length > 0 ? rule.rules : [{ label_pattern: '', score: 0, category: '' }]);
    } else {
      setEditingRule(null);
      setRuleName('');
      setRuleDescription('');
      setRuleItems([{ label_pattern: '', score: 0, category: '' }]);
    }
    setShowRuleModal(true);
  };

  // ルールアイテム追加
  const addRuleItem = () => {
    setRuleItems([...ruleItems, { label_pattern: '', score: 0, category: '' }]);
  };

  // ルールアイテム削除
  const removeRuleItem = (index: number) => {
    setRuleItems(ruleItems.filter((_, i) => i !== index));
  };

  // ルールアイテム更新
  const updateRuleItem = (index: number, field: keyof ScoringRuleItem, value: string | number) => {
    const newItems = [...ruleItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setRuleItems(newItems);
  };

  // ルール保存
  const saveRule = async () => {
    if (!selectedCompanyId || !ruleName) return;

    const validItems = ruleItems.filter((item) => item.label_pattern && item.score !== 0);
    if (validItems.length === 0) {
      alert('少なくとも1つの有効なルール項目が必要です');
      return;
    }

    const data: ScoringRuleCreate = {
      company_id: selectedCompanyId,
      name: ruleName,
      description: ruleDescription || undefined,
      rules: validItems,
    };

    try {
      if (editingRule) {
        await scoringApi.updateRule(editingRule.id, data);
      } else {
        await scoringApi.createRule(data);
      }
      setShowRuleModal(false);
      fetchRules();
    } catch (error) {
      console.error('ルール保存エラー:', error);
      alert('ルールの保存に失敗しました');
    }
  };

  // ルール削除
  const deleteRule = async (ruleId: number) => {
    if (!confirm('このスコアルールを削除しますか？')) return;
    try {
      await scoringApi.deleteRule(ruleId);
      fetchRules();
      if (selectedRuleId === ruleId) {
        setSelectedRuleId(null);
      }
    } catch (error) {
      console.error('ルール削除エラー:', error);
      alert('ルールの削除に失敗しました');
    }
  };

  // スコア計算実行
  const calculateScores = async () => {
    if (!selectedRuleId) return;
    setIsCalculating(true);
    try {
      const params: ScoringCalculateParams = {
        rule_id: selectedRuleId,
        start_date: startDate,
        end_date: endDate,
      };
      const result = await scoringApi.calculate(params);
      setScoreResult(result);
    } catch (error) {
      console.error('スコア計算エラー:', error);
      alert('スコア計算に失敗しました');
    } finally {
      setIsCalculating(false);
    }
  };

  // ランキング取得
  const fetchRanking = async () => {
    if (!selectedRuleId) return;
    setIsCalculating(true);
    try {
      const params: ScoringRankingParams = {
        rule_id: selectedRuleId,
        start_date: startDate,
        end_date: endDate,
        limit: 20,
      };
      const result = await scoringApi.getRanking(params);
      setRankingResult(result);
    } catch (error) {
      console.error('ランキング取得エラー:', error);
      alert('ランキング取得に失敗しました');
    } finally {
      setIsCalculating(false);
    }
  };

  // ビューモード切り替え時の処理
  useEffect(() => {
    if (viewMode === 'calculate' && selectedRuleId) {
      calculateScores();
    } else if (viewMode === 'ranking' && selectedRuleId) {
      fetchRanking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedRuleId]);

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6">行動スコアリング</h1>

      {/* フィルター */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">法人</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={selectedCompanyId || ''}
              onChange={(e) => setSelectedCompanyId(Number(e.target.value))}
            >
              <option value="">選択してください</option>
              {accessibleCompanies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.display_name || company.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">スコアルール</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={selectedRuleId || ''}
              onChange={(e) => setSelectedRuleId(Number(e.target.value))}
              disabled={rules.length === 0}
            >
              <option value="">選択してください</option>
              {rules.map((rule) => (
                <option key={rule.id} value={rule.id}>
                  {rule.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">開始日</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">終了日</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* タブ切り替え */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`px-4 py-2 font-medium ${
            viewMode === 'rules'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setViewMode('rules')}
        >
          ルール管理
        </button>
        <button
          className={`px-4 py-2 font-medium ${
            viewMode === 'calculate'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setViewMode('calculate')}
          disabled={!selectedRuleId}
        >
          スコア計算
        </button>
        <button
          className={`px-4 py-2 font-medium ${
            viewMode === 'ranking'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setViewMode('ranking')}
          disabled={!selectedRuleId}
        >
          ランキング
        </button>
      </div>

      {/* ルール管理ビュー */}
      {viewMode === 'rules' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold">スコアルール一覧</h2>
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              onClick={() => openRuleModal()}
              disabled={!selectedCompanyId}
            >
              新規作成
            </button>
          </div>
          <div className="p-4">
            {isLoadingRules ? (
              <div className="text-center py-8">読み込み中...</div>
            ) : rules.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                スコアルールがありません。新規作成してください。
              </div>
            ) : (
              <div className="space-y-4">
                {rules.map((rule) => (
                  <div key={rule.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-lg">{rule.name}</h3>
                        {rule.description && (
                          <p className="text-gray-600 text-sm">{rule.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          className="text-blue-600 hover:text-blue-800 text-sm"
                          onClick={() => openRuleModal(rule)}
                        >
                          編集
                        </button>
                        <button
                          className="text-red-600 hover:text-red-800 text-sm"
                          onClick={() => deleteRule(rule.id)}
                        >
                          削除
                        </button>
                      </div>
                    </div>
                    <div className="mt-3">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">ルール項目:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {rule.rules.map((item, idx) => (
                          <div
                            key={idx}
                            className="bg-gray-50 rounded px-3 py-2 text-sm flex justify-between"
                          >
                            <span className="text-gray-700">
                              {item.label_pattern}
                              {item.category && (
                                <span className="text-gray-500 ml-1">({item.category})</span>
                              )}
                            </span>
                            <span className="font-semibold text-blue-600">{item.score}点</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* スコア計算ビュー */}
      {viewMode === 'calculate' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold">スコア計算結果</h2>
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              onClick={calculateScores}
              disabled={isCalculating || !selectedRuleId}
            >
              {isCalculating ? '計算中...' : '再計算'}
            </button>
          </div>
          <div className="p-4">
            {!scoreResult ? (
              <div className="text-center py-8 text-gray-500">
                スコアルールを選択して計算してください
              </div>
            ) : (
              <div>
                {/* サマリー */}
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <div className="text-sm text-gray-600">ルール名</div>
                      <div className="font-semibold">{scoreResult.rule_name}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">期間</div>
                      <div className="font-semibold">
                        {scoreResult.period.start_date} 〜 {scoreResult.period.end_date}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">合計スコア</div>
                      <div className="font-bold text-2xl text-blue-600">
                        {scoreResult.total_score.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">項目数</div>
                      <div className="font-semibold">{scoreResult.items.length}件</div>
                    </div>
                  </div>
                </div>

                {/* カテゴリ別 */}
                {scoreResult.by_category.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-semibold mb-3">カテゴリ別スコア</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {scoreResult.by_category.map((cat) => (
                        <div
                          key={cat.category}
                          className="bg-gray-50 rounded-lg p-3 text-center"
                        >
                          <div className="text-sm text-gray-600">{cat.category}</div>
                          <div className="font-bold text-lg">
                            {cat.total_score.toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            {cat.percentage}% ({cat.item_count}件)
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 詳細テーブル */}
                <div>
                  <h3 className="font-semibold mb-3">詳細</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            ラベル名
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            パターン
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            カウント
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            単価
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            スコア
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {scoreResult.items.map((item, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-2 text-sm">{item.label_name}</td>
                            <td className="px-4 py-2 text-sm text-gray-500">
                              {item.label_pattern}
                            </td>
                            <td className="px-4 py-2 text-sm text-right">
                              {item.count.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-sm text-right">
                              {item.score_per_item}点
                            </td>
                            <td className="px-4 py-2 text-sm text-right font-semibold text-blue-600">
                              {item.total_score.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ランキングビュー */}
      {viewMode === 'ranking' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold">ステップ別スコアランキング</h2>
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              onClick={fetchRanking}
              disabled={isCalculating || !selectedRuleId}
            >
              {isCalculating ? '取得中...' : '更新'}
            </button>
          </div>
          <div className="p-4">
            {!rankingResult ? (
              <div className="text-center py-8 text-gray-500">
                スコアルールを選択してください
              </div>
            ) : rankingResult.rankings.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                該当するデータがありません
              </div>
            ) : (
              <div>
                {/* サマリー */}
                <div className="bg-green-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-sm text-gray-600">ルール名</div>
                      <div className="font-semibold">{rankingResult.rule_name}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">期間</div>
                      <div className="font-semibold">
                        {rankingResult.period.start_date} 〜 {rankingResult.period.end_date}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">総スコア</div>
                      <div className="font-bold text-2xl text-green-600">
                        {rankingResult.total_score.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ランキング */}
                <div className="space-y-4">
                  {rankingResult.rankings.map((item) => (
                    <div
                      key={item.rank}
                      className={`border rounded-lg p-4 ${
                        item.rank <= 3 ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-3">
                          <span
                            className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                              item.rank === 1
                                ? 'bg-yellow-500 text-white'
                                : item.rank === 2
                                ? 'bg-gray-400 text-white'
                                : item.rank === 3
                                ? 'bg-orange-400 text-white'
                                : 'bg-gray-200 text-gray-600'
                            }`}
                          >
                            {item.rank}
                          </span>
                          <div>
                            <span className="font-semibold">{item.step_name}</span>
                            {item.channel && (
                              <span className="ml-2 text-sm text-gray-500">
                                ({item.channel})
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="font-bold text-xl text-blue-600">
                          {item.total_score.toLocaleString()}点
                        </div>
                      </div>
                      {item.breakdown.length > 0 && (
                        <div className="mt-2 pl-11">
                          <div className="text-xs text-gray-500 mb-1">内訳:</div>
                          <div className="flex flex-wrap gap-2">
                            {item.breakdown.slice(0, 5).map((bd, idx) => (
                              <span
                                key={idx}
                                className="bg-white border border-gray-200 rounded px-2 py-1 text-xs"
                              >
                                {bd.label_pattern}: {bd.total_score.toLocaleString()}点
                              </span>
                            ))}
                            {item.breakdown.length > 5 && (
                              <span className="text-xs text-gray-500">
                                他{item.breakdown.length - 5}件
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ルール作成/編集モーダル */}
      {showRuleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">
                {editingRule ? 'スコアルール編集' : 'スコアルール作成'}
              </h2>
            </div>
            <div className="p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ルール名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={ruleName}
                  onChange={(e) => setRuleName(e.target.value)}
                  placeholder="例: 基本スコアルール"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">説明</label>
                <textarea
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={ruleDescription}
                  onChange={(e) => setRuleDescription(e.target.value)}
                  rows={2}
                  placeholder="ルールの説明（任意）"
                />
              </div>
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    ルール項目 <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    className="text-blue-600 hover:text-blue-800 text-sm"
                    onClick={addRuleItem}
                  >
                    + 項目追加
                  </button>
                </div>
                <div className="space-y-2">
                  {ruleItems.map((item, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-12 gap-2 items-center bg-gray-50 p-2 rounded"
                    >
                      <div className="col-span-5">
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                          value={item.label_pattern}
                          onChange={(e) => updateRuleItem(idx, 'label_pattern', e.target.value)}
                          placeholder="ラベルパターン"
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number"
                          className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                          value={item.score}
                          onChange={(e) => updateRuleItem(idx, 'score', Number(e.target.value))}
                          placeholder="スコア"
                        />
                      </div>
                      <div className="col-span-4">
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                          value={item.category || ''}
                          onChange={(e) => updateRuleItem(idx, 'category', e.target.value)}
                          placeholder="カテゴリ（任意）"
                        />
                      </div>
                      <div className="col-span-1 text-center">
                        {ruleItems.length > 1 && (
                          <button
                            type="button"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => removeRuleItem(idx)}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  パターンはラベル名の部分一致で検索されます（例: 「タップ」は「LINE1_オプトイン_タップ」にマッチ）
                </p>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                onClick={() => setShowRuleModal(false)}
              >
                キャンセル
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                onClick={saveRule}
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
